import { env } from '@/lib/env'

type Level = 'debug' | 'info' | 'warn' | 'error'

const RANK: Record<Level | 'silent', number> = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
  silent: 100,
}

const threshold = RANK[env.logLevel]

function emit(level: Level, message: string, context?: unknown): void {
  if (RANK[level] < threshold) return

  const prefix = `[${env.appEnv}] ${message}`
  /* eslint-disable no-console */
  switch (level) {
    case 'debug':
      console.debug(prefix, context ?? '')
      break
    case 'info':
      console.info(prefix, context ?? '')
      break
    case 'warn':
      console.warn(prefix, context ?? '')
      break
    case 'error':
      console.error(prefix, context ?? '')
      break
  }
  /* eslint-enable no-console */
}

/**
 * Central logger. In production this is the seam where a remote sink
 * (App Insights / Sentry) gets wired in — see `reportError`.
 */
export const logger = {
  debug: (message: string, context?: unknown) => {
    emit('debug', message, context)
  },
  info: (message: string, context?: unknown) => {
    emit('info', message, context)
  },
  warn: (message: string, context?: unknown) => {
    emit('warn', message, context)
  },
  error: (message: string, context?: unknown) => {
    emit('error', message, context)
  },
}

/**
 * Single funnel for unhandled/boundary errors. Wire your telemetry provider
 * here once instead of scattering SDK calls through the component tree.
 */
export function reportError(error: unknown, context?: Record<string, unknown>): void {
  logger.error(error instanceof Error ? error.message : 'Unknown error', {
    ...context,
    stack: error instanceof Error ? error.stack : undefined,
  })

  if (env.isProduction) {
    // TODO: forward to Application Insights / Sentry.
  }
}
